import * as XLSX from 'xlsx';
import { 
  LeadConversionReport, 
  AgentPerformanceReport, 
  ConversationAnalyticsReport, 
  UserActivityReport 
} from '../types/reports';

export class ReportExporter {
  static exportToExcel(
    data: LeadConversionReport | AgentPerformanceReport | ConversationAnalyticsReport | UserActivityReport,
    reportType: string,
    filename?: string
  ) {
    const workbook = XLSX.utils.book_new();
    
    // Add summary sheet
    const summaryData = this.getSummaryData(data, reportType);
    const summarySheet = XLSX.utils.json_to_sheet([summaryData]);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Resumo');

    // Add detailed sheets based on report type
    switch (reportType) {
      case 'LeadConversion':
        this.addLeadConversionSheets(workbook, data as LeadConversionReport);
        break;
      case 'AgentPerformance':
        this.addAgentPerformanceSheets(workbook, data as AgentPerformanceReport);
        break;
      case 'ConversationAnalytics':
        this.addConversationAnalyticsSheets(workbook, data as ConversationAnalyticsReport);
        break;
      case 'UserActivity':
        this.addUserActivitySheets(workbook, data as UserActivityReport);
        break;
    }

    // Generate filename
    const defaultFilename = `${reportType}_${new Date().toISOString().split('T')[0]}.xlsx`;
    const finalFilename = filename || defaultFilename;

    // Download file
    XLSX.writeFile(workbook, finalFilename);
  }

  static exportToCSV(
    data: LeadConversionReport | AgentPerformanceReport | ConversationAnalyticsReport | UserActivityReport,
    reportType: string,
    filename?: string
  ) {
    const csvData = this.getCSVData(data, reportType);
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      const defaultFilename = `${reportType}_${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute('download', filename || defaultFilename);
      
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  private static getSummaryData(data: any, reportType: string) {
    const baseData = {
      'Tipo de Relatório': reportType,
      'Data de Geração': data.generatedAt,
      'Data Inicial': data.startDate || 'N/A',
      'Data Final': data.endDate || 'N/A',
      'Total de Registros': data.totalRecords
    };

    switch (reportType) {
      case 'LeadConversion':
        return {
          ...baseData,
          'Total de Leads': data.totalLeads,
          'Leads Atribuídos': data.assignedLeads,
          'Leads Não Atribuídos': data.unassignedLeads,
          'Leads Integrados': data.integratedLeads,
          'Taxa de Conversão (%)': data.conversionRate,
          'Taxa de Atribuição (%)': data.assignmentRate,
          'Taxa de Integração (%)': data.integrationRate
        };
      case 'AgentPerformance':
        return {
          ...baseData,
          'Total de Agentes': data.totalAgents,
          'Agentes Ativos': data.activeAgents,
          'Agentes Desabilitados': data.disabledAgents,
          'Tempo Médio de Resposta (ms)': data.averageResponseTime,
          'Total de Conversas': data.totalConversations,
          'Conversas Concluídas': data.completedConversations,
          'Taxa de Conclusão (%)': data.completionRate
        };
      case 'ConversationAnalytics':
        return {
          ...baseData,
          'Total de Conversas': data.totalConversations,
          'Conversas Ativas': data.activeConversations,
          'Conversas Concluídas': data.completedConversations,
          'Conversas Não Atendidas': data.unattendedConversations,
          'Média de Mensagens por Conversa': data.averageMessagesPerConversation,
          'Tempo Médio de Resposta (ms)': data.averageResponseTime
        };
      case 'UserActivity':
        return {
          ...baseData,
          'Total de Usuários': data.totalUsers,
          'Usuários Ativos': data.activeUsers,
          'Taxa de Atividade (%)': data.totalUsers > 0 ? ((data.activeUsers / data.totalUsers) * 100).toFixed(2) : 0
        };
      default:
        return baseData;
    }
  }

  private static addLeadConversionSheets(workbook: XLSX.WorkBook, data: LeadConversionReport) {
    // Quality breakdown sheet
    if (data.qualityBreakdown && data.qualityBreakdown.length > 0) {
      const qualitySheet = XLSX.utils.json_to_sheet(data.qualityBreakdown);
      XLSX.utils.book_append_sheet(workbook, qualitySheet, 'Qualidade dos Leads');
    }

    // Status breakdown sheet
    if (data.statusBreakdown && data.statusBreakdown.length > 0) {
      const statusSheet = XLSX.utils.json_to_sheet(data.statusBreakdown);
      XLSX.utils.book_append_sheet(workbook, statusSheet, 'Status dos Leads');
    }

    // Daily stats sheet
    if (data.dailyStats && data.dailyStats.length > 0) {
      const dailySheet = XLSX.utils.json_to_sheet(data.dailyStats);
      XLSX.utils.book_append_sheet(workbook, dailySheet, 'Estatísticas Diárias');
    }

    // Agent stats sheet
    if (data.agentStats && data.agentStats.length > 0) {
      const agentSheet = XLSX.utils.json_to_sheet(data.agentStats);
      XLSX.utils.book_append_sheet(workbook, agentSheet, 'Performance dos Agentes');
    }
  }

  private static addAgentPerformanceSheets(workbook: XLSX.WorkBook, data: AgentPerformanceReport) {
    // Agent stats sheet
    if (data.agentStats && data.agentStats.length > 0) {
      const agentSheet = XLSX.utils.json_to_sheet(data.agentStats);
      XLSX.utils.book_append_sheet(workbook, agentSheet, 'Detalhes dos Agentes');
    }

    // Daily stats sheet
    if (data.dailyStats && data.dailyStats.length > 0) {
      const dailySheet = XLSX.utils.json_to_sheet(data.dailyStats);
      XLSX.utils.book_append_sheet(workbook, dailySheet, 'Estatísticas Diárias');
    }

    // Type product breakdown sheet
    if (data.typeProductBreakdown && data.typeProductBreakdown.length > 0) {
      const typeSheet = XLSX.utils.json_to_sheet(data.typeProductBreakdown);
      XLSX.utils.book_append_sheet(workbook, typeSheet, 'Distribuição por Tipo');
    }
  }

  private static addConversationAnalyticsSheets(workbook: XLSX.WorkBook, data: ConversationAnalyticsReport) {
    // Status breakdown sheet
    if (data.statusBreakdown && data.statusBreakdown.length > 0) {
      const statusSheet = XLSX.utils.json_to_sheet(data.statusBreakdown);
      XLSX.utils.book_append_sheet(workbook, statusSheet, 'Status das Conversas');
    }

    // Daily stats sheet
    if (data.dailyStats && data.dailyStats.length > 0) {
      const dailySheet = XLSX.utils.json_to_sheet(data.dailyStats);
      XLSX.utils.book_append_sheet(workbook, dailySheet, 'Estatísticas Diárias');
    }

    // Agent stats sheet
    if (data.agentStats && data.agentStats.length > 0) {
      const agentSheet = XLSX.utils.json_to_sheet(data.agentStats);
      XLSX.utils.book_append_sheet(workbook, agentSheet, 'Performance dos Agentes');
    }

    // Hourly activity sheet
    if (data.hourlyActivity && data.hourlyActivity.length > 0) {
      const hourlySheet = XLSX.utils.json_to_sheet(data.hourlyActivity);
      XLSX.utils.book_append_sheet(workbook, hourlySheet, 'Atividade por Hora');
    }
  }

  private static addUserActivitySheets(workbook: XLSX.WorkBook, data: UserActivityReport) {
    // User stats sheet
    if (data.userStats && data.userStats.length > 0) {
      const userSheet = XLSX.utils.json_to_sheet(data.userStats);
      XLSX.utils.book_append_sheet(workbook, userSheet, 'Detalhes dos Usuários');
    }

    // Daily activity sheet
    if (data.dailyActivity && data.dailyActivity.length > 0) {
      const dailySheet = XLSX.utils.json_to_sheet(data.dailyActivity);
      XLSX.utils.book_append_sheet(workbook, dailySheet, 'Atividade Diária');
    }

    // Role breakdown sheet
    if (data.roleBreakdown && data.roleBreakdown.length > 0) {
      const roleSheet = XLSX.utils.json_to_sheet(data.roleBreakdown);
      XLSX.utils.book_append_sheet(workbook, roleSheet, 'Distribuição por Função');
    }
  }

  private static getCSVData(data: any, reportType: string): string {
    const summaryData = this.getSummaryData(data, reportType);
    const headers = Object.keys(summaryData);
    const values = Object.values(summaryData);
    
    let csv = headers.join(',') + '\n';
    csv += values.map(value => `"${value}"`).join(',') + '\n';
    
    return csv;
  }
}
